import config from '../config';
import EventEmitter from 'eventemitter3';

type WebSocketEvent = 'connected' | 'disconnected' | 'message' | 'error' | 'orderUpdate' | 'positionUpdate' | 'marketDataUpdate' | 'presetExecutionUpdate';

export class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 2000; // Start with 2 seconds
  private eventEmitter: EventEmitter = new EventEmitter();
  private isConnected = false;
  private connectionPromise: Promise<void> | null = null;
  private resolveConnectionPromise: (() => void) | null = null;

  // Store information about subscribed topics
  private subscriptions: Set<string> = new Set();
  private marketDataSubscriptions: Set<string> = new Set();
  private presetSubscriptions: Set<string> = new Set();

  constructor() {
    this.eventEmitter = new EventEmitter();
  }

  /**
   * Connect to the WebSocket server
   */
  connect(): Promise<void> {
    if (this.isConnected) {
      return Promise.resolve();
    }

    if (this.connectionPromise) {
      return this.connectionPromise;
    }

    this.connectionPromise = new Promise<void>((resolve) => {
      this.resolveConnectionPromise = resolve;

      try {
        const wsUrl = `${config.wsBaseUrl}/api/v1/ws/orders`;
        this.socket = new WebSocket(wsUrl);
        
        this.socket.onopen = this.handleOpen;
        this.socket.onmessage = this.handleMessage;
        this.socket.onclose = this.handleClose;
        this.socket.onerror = this.handleError;
      } catch (error) {
        console.error('Error creating WebSocket connection:', error);
        this.handleError(new Event('error'));
      }
    });

    return this.connectionPromise;
  }

  /**
   * Connect to order updates for a specific symbol
   */
  connectToSymbol(symbol: string): Promise<void> {
    const topic = `orders_${symbol.toUpperCase()}`;
    this.subscriptions.add(topic);

    if (this.isConnected) {
      // We're already connected, so just subscribe to the symbol
      this.send({ action: 'subscribe', topic });
      return Promise.resolve();
    }

    // Connect first, then subscribe
    return this.connect().then(() => {
      this.send({ action: 'subscribe', topic });
    });
  }

  /**
   * Subscribe to market data updates for a specific symbol
   */
  async subscribeToMarketData(symbol: string): Promise<void> {
    const symbolUpper = symbol.toUpperCase();
    
    // Add to our tracking
    this.marketDataSubscriptions.add(symbolUpper);

    // Ensure we're connected
    await this.connect();

    // Send subscription message
    this.send({ 
      action: 'subscribe', 
      symbol: symbolUpper
    });

    console.log(`Subscribed to market data for ${symbolUpper}`);
  }

  /**
   * Unsubscribe from market data updates for a specific symbol
   */
  unsubscribeFromMarketData(symbol: string): void {
    const symbolUpper = symbol.toUpperCase();
    
    // Remove from our tracking
    this.marketDataSubscriptions.delete(symbolUpper);

    if (this.isConnected) {
      // Send unsubscription message
      this.send({ 
        action: 'unsubscribe', 
        symbol: symbolUpper
      });

      console.log(`Unsubscribed from market data for ${symbolUpper}`);
    }
  }

  /**
   * Subscribe to preset execution monitoring
   */
  async subscribeToPresetMonitor(): Promise<void> {
    // Ensure we're connected
    await this.connect();

    // Connect to preset monitor WebSocket (different endpoint)
    if (this.socket) {
      // For now, we'll handle this through the main connection
      // In a production system, you might want separate connections
      this.send({ action: 'subscribe_monitor' });
      console.log('Subscribed to preset monitoring');
    }
  }

  /**
   * Subscribe to specific preset execution updates
   */
  subscribeToPreset(presetId: string): void {
    this.presetSubscriptions.add(presetId);

    if (this.isConnected) {
      this.send({ 
        action: 'subscribe_preset', 
        preset_id: presetId
      });
      console.log(`Subscribed to preset ${presetId}`);
    }
  }

  /**
   * Unsubscribe from specific preset execution updates
   */
  unsubscribeFromPreset(presetId: string): void {
    this.presetSubscriptions.delete(presetId);

    if (this.isConnected) {
      this.send({ 
        action: 'unsubscribe_preset', 
        preset_id: presetId
      });
      console.log(`Unsubscribed from preset ${presetId}`);
    }
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect(): void {
    this.clearReconnectTimer();
    
    if (this.socket) {
      // Remove event listeners
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      
      // Close the connection
      this.socket.close();
      this.socket = null;
    }
    
    this.isConnected = false;
    this.connectionPromise = null;
    this.resolveConnectionPromise = null;
    this.subscriptions.clear();
    this.marketDataSubscriptions.clear();
    this.presetSubscriptions.clear();
    this.eventEmitter.emit('disconnected');
  }

  /**
   * Send a message to the WebSocket server
   */
  send(data: any): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send message: WebSocket is not open');
      return;
    }

    this.socket.send(JSON.stringify(data));
  }

  /**
   * Subscribe to WebSocket events
   */
  on(event: WebSocketEvent, listener: (...args: any[]) => void): void {
    this.eventEmitter.on(event, listener);
  }

  /**
   * Unsubscribe from WebSocket events
   */
  off(event: WebSocketEvent, listener: (...args: any[]) => void): void {
    this.eventEmitter.off(event, listener);
  }

  /**
   * Get current subscriptions status
   */
  getSubscriptionsStatus() {
    return {
      connected: this.isConnected,
      orderSubscriptions: Array.from(this.subscriptions),
      marketDataSubscriptions: Array.from(this.marketDataSubscriptions),
      presetSubscriptions: Array.from(this.presetSubscriptions)
    };
  }

  /**
   * Resubscribe to all topics after reconnection
   */
  private resubscribe(): void {
    if (!this.isConnected || !this.socket) return;

    // Resubscribe to order topics
    this.subscriptions.forEach((topic) => {
      this.send({ action: 'subscribe', topic });
    });

    // Resubscribe to market data
    this.marketDataSubscriptions.forEach((symbol) => {
      this.send({ action: 'subscribe', symbol });
    });

    // Resubscribe to preset monitoring
    this.presetSubscriptions.forEach((presetId) => {
      this.send({ action: 'subscribe_preset', preset_id: presetId });
    });

    console.log('Resubscribed to all topics after reconnection');
  }

  /**
   * Handle WebSocket open event
   */
  private handleOpen = (): void => {
    console.log('WebSocket connection established');
    this.isConnected = true;
    this.reconnectAttempts = 0;
    this.eventEmitter.emit('connected');
    
    if (this.resolveConnectionPromise) {
      this.resolveConnectionPromise();
      this.resolveConnectionPromise = null;
    }

    // Resubscribe to topics if needed
    this.resubscribe();
  };

  /**
   * Handle WebSocket message event
   */
  private handleMessage = (event: MessageEvent): void => {
    try {
      const data = JSON.parse(event.data);
      
      // Handle different message types
      if (data.type === 'order_update' || data.type === 'order_executed') {
        this.eventEmitter.emit('orderUpdate', data.data);
      } else if (data.type === 'position_update') {
        this.eventEmitter.emit('positionUpdate', data.data);
      } else if (data.type === 'market_data_update') {
        this.eventEmitter.emit('marketDataUpdate', data.data);
      } else if (data.type === 'preset_execution_update') {
        this.eventEmitter.emit('presetExecutionUpdate', data.data);
      } else if (data.type === 'connection_established') {
        console.log('WebSocket connection confirmed with ID:', data.connection_id);
      } else if (data.type === 'subscribed') {
        console.log(`Successfully subscribed to ${data.symbol} market data`);
      } else if (data.type === 'unsubscribed') {
        console.log(`Successfully unsubscribed from ${data.symbol} market data`);
      } else if (data.type === 'subscribed_preset') {
        console.log(`Successfully subscribed to preset ${data.preset_id}`);
      } else if (data.type === 'unsubscribed_preset') {
        console.log(`Successfully unsubscribed from preset ${data.preset_id}`);
      }
      
      // Always emit the raw message too
      this.eventEmitter.emit('message', data);
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  };

  /**
   * Handle WebSocket close event
   */
  private handleClose = (event: CloseEvent): void => {
    this.isConnected = false;
    this.socket = null;
    
    console.log(`WebSocket connection closed (${event.code}): ${event.reason}`);
    this.eventEmitter.emit('disconnected');

    // Attempt to reconnect if not closed deliberately
    if (event.code !== 1000) { // Normal Closure
      this.attemptReconnect();
    }
  };

  /**
   * Handle WebSocket error event
   */
  private handleError = (event: Event): void => {
    console.error('WebSocket error:', event);
    this.eventEmitter.emit('error', event);
    
    // Error might be followed by close event, but just in case:
    if (this.isConnected) {
      this.isConnected = false;
      this.attemptReconnect();
    }
  };

  /**
   * Attempt to reconnect to the WebSocket server
   */
  private attemptReconnect(): void {
    // Don't attempt to reconnect if we've reached the maximum number of attempts
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(`Failed to reconnect after ${this.maxReconnectAttempts} attempts`);
      return;
    }

    this.clearReconnectTimer();

    // Use exponential backoff
    const delay = this.reconnectDelay * Math.pow(1.5, this.reconnectAttempts);
    
    console.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  /**
   * Clear the reconnect timer
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }
}

// Create a singleton instance
export const websocketService = new WebSocketService();

export default websocketService; 